/**
 * Lazy Chart Renderer
 * 
 * Optimized chart rendering that only loads Recharts when needed.
 * This reduces the initial bundle size by ~400KB for messages without charts.
 */

import React, { useState, Suspense } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Types
interface ChartData {
  type: 'bar' | 'line' | 'pie';
  title?: string;
  data: Array<{ name: string; value: number }>;
}

interface LazyChartRendererProps {
  chartData: ChartData;
  isDarkMode?: boolean;
  density?: 'compact' | 'comfortable' | 'spacious';
}

const COLORS = ['#fbbf24', '#3b82f6', '#10b981', '#f43f5e', '#8b5cf6'];

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, isDarkMode }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className={`p-3 border-2 shadow-xl rounded-lg ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-300'}`}>
        <p className={`text-xs font-bold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{label}</p>
        <p className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
          {payload[0].value?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
        </p>
        <p className={`text-xs mt-1 font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>Click for details</p>
      </div>
    );
  }
  return null;
};

// Loading skeleton while chart loads
export const ChartSkeleton: React.FC<{ isDarkMode?: boolean }> = ({ isDarkMode }) => (
  <div className={`my-6 p-4 rounded-xl shadow-md border-2 animate-pulse ${
    isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-300'
  }`}>
    <div className={`h-4 rounded w-1/3 mb-4 ${isDarkMode ? 'bg-slate-600' : 'bg-slate-200'}`} />
    <div className={`h-64 rounded ${isDarkMode ? 'bg-slate-600' : 'bg-slate-200'}`} />
  </div>
);

const LazyChartRenderer: React.FC<LazyChartRendererProps> = ({ 
  chartData, 
  isDarkMode = true, 
  density = 'comfortable' 
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  
  const chartHeight = density === 'compact' 
    ? 'h-48 sm:h-56' 
    : density === 'spacious' 
      ? 'h-80 sm:h-96' 
      : 'h-64 sm:h-72';

  const onChartClick = (_data: any, index: number) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  if (!chartData || !chartData.data) return null;

  return (
    <div className={`my-6 p-4 rounded-xl shadow-md hover:shadow-lg transition-shadow border-2 ${
      isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-300'
    }`}>
      <div className="flex justify-between items-center mb-4">
        <h4 className={`text-sm sm:text-base font-bold uppercase tracking-wide ${
          isDarkMode ? 'text-slate-100' : 'text-slate-800'
        }`}>
          {chartData.title || "Financial Analysis"}
        </h4>
        <div className={`text-xs px-3 py-1 rounded-full border ${
          isDarkMode ? 'text-amber-400 bg-amber-500/10 border-amber-500/30' : 'text-amber-700 bg-amber-50 border-amber-200'
        }`}>
          Interactive
        </div>
      </div>
      
      <div className={`${chartHeight} w-full`}>
        <ResponsiveContainer width="100%" height="100%">
          {chartData.type === 'line' ? (
            <LineChart 
              data={chartData.data} 
              onClick={(e) => { 
                if (e && e.activeTooltipIndex !== undefined) {
                  setActiveIndex(Number(e.activeTooltipIndex)); 
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" 
                stroke="#64748b" 
                fontSize={11} 
                tickLine={false} 
                axisLine={false} 
                dy={10} 
              />
              <YAxis 
                stroke="#64748b" 
                fontSize={11} 
                tickLine={false} 
                axisLine={false} 
                tickFormatter={(val) => val.toLocaleString()} 
              />
              <Tooltip content={<CustomTooltip isDarkMode={isDarkMode} />} />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#fbbf24"
                strokeWidth={3}
                dot={{ r: 4, fill: '#fbbf24', strokeWidth: 2, stroke: '#fff' }}
                activeDot={{ r: 7, strokeWidth: 0 }}
                animationDuration={1500}
              />
            </LineChart>
          ) : chartData.type === 'pie' ? (
            <PieChart>
              <Pie
                data={chartData.data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                fill="#8884d8"
                paddingAngle={5}
                dataKey="value"
                onClick={onChartClick}
                cursor="pointer"
              >
                {chartData.data.map((_entry: any, index: number) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                    stroke={activeIndex === index ? '#1e293b' : 'none'}
                    strokeWidth={activeIndex === index ? 2 : 0}
                    opacity={activeIndex !== null && activeIndex !== index ? 0.5 : 1}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip isDarkMode={isDarkMode} />} />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          ) : (
            <BarChart data={chartData.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis 
                dataKey="name" 
                stroke="#64748b" 
                fontSize={11} 
                tickLine={false} 
                axisLine={false} 
                dy={10} 
              />
              <YAxis 
                stroke="#64748b" 
                fontSize={11} 
                tickLine={false} 
                axisLine={false} 
                tickFormatter={(val) => val.toLocaleString()} 
              />
              <Tooltip
                cursor={{ fill: '#f1f5f9' }}
                content={<CustomTooltip isDarkMode={isDarkMode} />}
              />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              <Bar
                dataKey="value"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
                onClick={onChartClick}
                cursor="pointer"
                animationDuration={1500}
              >
                {chartData.data.map((_entry: any, index: number) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={activeIndex === index ? '#f59e0b' : '#fbbf24'}
                    opacity={activeIndex !== null && activeIndex !== index ? 0.6 : 1}
                  />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LazyChartRenderer;
